import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import API from "../api";
import { showError, showSuccess } from "../utils/dialog";
import "../App.css";
import talentLogo2 from "../assets/talentlogo2.png";

function getPasswordError(password) {
  if (password.length < 8) return "Password must be at least 8 characters.";
  if (!/[a-z]/.test(password))
    return "Password must include at least one lowercase letter.";
  if (!/[A-Z]/.test(password))
    return "Password must include at least one uppercase letter.";
  if (!/\d/.test(password)) return "Password must include at least one number.";
  if (!/[^A-Za-z0-9]/.test(password))
    return "Password must include at least one special character.";
  return "";
}

export default function AcceptInvite() {
  usePageMeta("Activate Account");
  const { guid } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invite, setInvite] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const res = await API.get(`/auth/invite/${encodeURIComponent(guid || "")}`);
        const data = res?.data?.invite;
        if (!mounted) return;
        if (!data) {
          setErrorMessage("Invite not found.");
          return;
        }
        setInvite(data);
        setForm((prev) => ({
          ...prev,
          first_name: data.first_name || "",
          last_name: data.last_name || "",
        }));
      } catch (err) {
        if (!mounted) return;
        const message = err?.response?.data?.message || "Invite is invalid or expired.";
        setErrorMessage(message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [guid]);

  const passwordError = useMemo(
    () => getPasswordError(form.password || ""),
    [form.password],
  );
  const confirmPasswordError =
    form.confirmPassword && form.confirmPassword !== form.password
      ? "Passwords do not match."
      : "";
  const canSubmit =
    !!form.first_name.trim() &&
    !!form.last_name.trim() &&
    !!form.password &&
    !passwordError &&
    !confirmPasswordError;

  const setField = (key, value) =>
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    try {
      setSubmitting(true);
      await API.post(`/auth/invite/${encodeURIComponent(guid || "")}/accept`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        password: form.password,
      });
      showSuccess("Account activated. You can now sign in.");
      navigate("/login");
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to activate account.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="background-overlay"></div>
      <div className="form-container">
        <div className="form-card">
          <div className="atlas-logo">
            <div className="flex items-start gap-6">
              <img src={talentLogo2} alt="Talent Logo" className="h-14 w-auto" />
            </div>
          </div>

          <h1 className="form-title">Activate Account</h1>

          {loading && <p>Validating invite...</p>}

          {!loading && errorMessage && (
            <div>
              <p className="error-text">{errorMessage}</p>
              <p className="form-footer">
                Go to <Link className="toggle-form" to="/login">Sign In</Link>
              </p>
            </div>
          )}

          {!loading && !errorMessage && invite && (
            <form className="auth-form" onSubmit={onSubmit}>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={invite.email || ""} readOnly />
              </div>

              <div className="form-group">
                <label className="form-label">First Name*</label>
                <input
                  className="form-input"
                  type="text"
                  value={form.first_name}
                  onChange={(e) => setField("first_name", e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Last Name*</label>
                <input
                  className="form-input"
                  type="text"
                  value={form.last_name}
                  onChange={(e) => setField("last_name", e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Password*</label>
                <div className="password-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-input"
                    value={form.password}
                    onChange={(e) => setField("password", e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? "🙈" : "👁️"}
                  </button>
                </div>
                {form.password && passwordError && (
                  <p className="error-text">{passwordError}</p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Confirm Password*</label>
                <div className="password-wrapper">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className="form-input"
                    value={form.confirmPassword}
                    onChange={(e) => setField("confirmPassword", e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                  >
                    {showConfirmPassword ? "🙈" : "👁️"}
                  </button>
                </div>
                {confirmPasswordError && (
                  <p className="error-text">{confirmPasswordError}</p>
                )}
              </div>

              <button
                type="submit"
                className={`submit-button ${submitting ? "loading" : ""}`}
                disabled={!canSubmit || submitting}
              >
                {submitting ? "Activating..." : "Activate Account"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
